import React from 'react';

import {SvgIconReact, SvgIconType} from 'common/components/svg-icon';
import {Translatable} from 'common/i18n';
import {useTranslation} from 'common/i18n/hooks';
import {react2panel} from 'common/util/react2panel';
import {withStyles} from 'common/util/react';
import {MarkdownReact} from 'common/components/markdown';
import {DEFAULT_SEARCH_QUERY_STRING} from 'common/report/util/search';
import {SearchSortOption} from 'common/report/util/search/types';

import {promptFeedbackModal} from '../feedback-modal';
import {FeedbackForFeature} from '../feedback-modal/types';
import {EmptyDashboardCreateHero} from '../warning-hero/presets-react';
import type {Browse} from './index';
import styles from './empty-state.module.styl';
import css from './empty-state.module.styl?inline';

type EmptyStateProps = {
  title?: Translatable;
  subtitle?: Translatable;
  icon?: string;
  children?: React.ReactNode;
};

// eslint-disable-next-line react/destructuring-assignment
const EmptyState = ({
  title = ({t}) => t(`discover.no-results.title`),
  subtitle,
  icon = `search`,
  children,
}: EmptyStateProps) => {
  const {print} = useTranslation();

  return (
    <div className={styles.browseEmptyState}>
      <div className={styles.browseEmptyIcon}>
        <SvgIconReact icon={icon as SvgIconType} />
      </div>
      <div className={styles.browseEmptyTitle}>{print(title)}</div>
      {subtitle ? <div className={styles.browseEmptySubtitle}>{print(subtitle)}</div> : null}
      <div className={styles.browseEmptyDetails}>{children}</div>
    </div>
  );
};

export const EmptyStateReact = withStyles(EmptyState, css);

const ErrorEmptyState = () => {
  const {t} = useTranslation();

  return (
    <>
      <EmptyStateReact title={t(`discover.error.title`)} subtitle={t(`discover.error.subtitle`)} icon='warning'>
        <MarkdownReact
          text={t(`discover.error.body`, {
            supportLink: `https://mixpanel.com/get-support`,
            statusLink: `https://www.mixpanelstatus.com/`,
            interpolation: {escapeValue: false},
          })}
        />
      </EmptyStateReact>
    </>
  );
};

export const ErrorEmptyStateEl = react2panel(`browse-error-empty-state`, ErrorEmptyState);

// eslint-disable-next-line react/destructuring-assignment
export const NoResultsEmptyState = ({cmp}: {cmp: Browse}) => {
  const hasFilteredResults =
    cmp.params.searchText !== DEFAULT_SEARCH_QUERY_STRING ||
    cmp.state.queryParams.sort === SearchSortOption.RecentlyViewed ||
    cmp.getNumActiveFilters() > 0;

  if (!hasFilteredResults) {
    if (cmp.isFullPageMode()) {
      return (
        <EmptyDashboardCreateHero
          onCreateDashboardClick={cmp.params.onCreateDashboardClicked}
          onOpenTemplateModalClick={cmp.params.onOpenTemplateModalClicked}
          onOpenFoundationalTemplateModalClicked={cmp.params.onOpenFoundationalTemplateModalClicked}
          shouldUseFoundationalTemplates={cmp.params.shouldUseFoundationalTemplates}
          headerText={cmp.t(`discover.no-results.full-page.title`)}
          bodyText={cmp.t(`discover.no-results.full-page.body`)}
        />
      );
    }

    if (cmp.isAddMode()) {
      return (
        <EmptyStateReact
          title={cmp.t(`discover.no-results.add-mode.title`)}
          subtitle={cmp.t(`discover.no-results.add-mode.subtitle`)}
          icon={`report`}
        />
      );
    }

    return <EmptyStateReact title={cmp.t(`discover.no-results.no-entities`)} />;
  } else {
    if (cmp.isFullPageMode()) {
      return (
        <EmptyStateReact subtitle={cmp.t(`discover.no-results.full-page.subtitle-filtered`)}>
          <a
            className='.mp-link'
            onClick={() => {
              promptFeedbackModal({feature: FeedbackForFeature.Browse});
            }}
          >
            {cmp.t(`discover.no-results.full-page.body-filtered`)}
          </a>
        </EmptyStateReact>
      );
    }
    return <EmptyStateReact subtitle={cmp.t(`discover.no-results.add-mode.subtitle-filtered`)} />;
  }
};

export const NoResultsEmptyStateEl = react2panel(`browse-error-empty-state`, NoResultsEmptyState);
